import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box, Button, Container, Divider, Paper, Tab, Tabs, TextField, Typography, Alert
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import BrandMark from '../../components/common/BrandMark';
import { afterSignInPath } from '../../utils/pendingInvite';

const LoginPage = () => {
  const {
    loginWithGoogle, loginWithCredentials, register, startDemo, isDemoMode, isAuthenticated, isLoading,
  } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);

  const isSignIn = tab === 0;

  // The landing page's "Sign in" comes here rather than to the app's home, because the home
  // reopens whatever session the browser holds, and after trying the builder that is a demo.
  // Somebody already signed in to a real account has nothing to do here, so they go on to their
  // flows; a demo session stays, because signing in is exactly what it came to do. Not after a
  // sign-in on this page: handleSubmit sends them on itself, and afterSignInPath clears the
  // pending invitation as it reads it, so a second read would lose it.
  const submitted = useRef(false);
  useEffect(() => {
    if (!submitted.current && !isLoading && isAuthenticated && !isDemoMode) {
      navigate(afterSignInPath('/'), { replace: true });
    }
  }, [isLoading, isAuthenticated, isDemoMode, navigate]);

  // Someone who came straight to the app URL gets the same deal as the landing page: start
  // building now, sign up when you want it on your own number. No account, no wall.
  const startWithoutAnAccount = async () => {
    setError('');
    setStarting(true);
    const result = await startDemo();
    if (result.success) {
      navigate('/flow-builder?new=1');
      return;
    }
    setError(result.error);
    setStarting(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    submitted.current = true;
    setError('');
    setLoading(true);

    const result = isSignIn
      ? await loginWithCredentials(email, password)
      : await register(email, fullName, password);

    if (result.success) {
      // Straight on to the invitation, if that is what brought them here.
      navigate(afterSignInPath('/'));
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <Container maxWidth="sm">
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="100vh">
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.2, mb: 0.5 }}>
            <BrandMark size={34} />
            <Typography variant="h4" component="h1" sx={{ letterSpacing: '-0.02em' }}>
              <Box component="span" sx={{ fontWeight: 500 }}>Flow</Box>
              <Box component="span" sx={{ fontWeight: 700, color: 'primary.main' }}>
                Gate
              </Box>
            </Typography>
          </Box>
          <Typography variant="subtitle1" gutterBottom align="center" color="text.secondary">
            WhatsApp Flow Builder
          </Typography>

          <Tabs value={tab} onChange={(_, v) => { setTab(v); setError(''); }} sx={{ mt: 3, mb: 2 }} centered>
            <Tab label="Sign in" />
            <Tab label="Create account" />
          </Tabs>

          {/* They are already using FlowGate; an account is what puts it on their own number.
              Saying so removes the fear that signing up means starting again. */}
          {isDemoMode && !isSignIn && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Your flows come with you. Everything you have built stays exactly where it is.
            </Alert>
          )}

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {!isSignIn && (
              <TextField
                label="Full name"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                fullWidth
                autoComplete="name"
              />
            )}
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              fullWidth
              autoComplete="email"
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              fullWidth
              inputProps={{ minLength: 8 }}
              helperText={!isSignIn ? 'Minimum 8 characters' : undefined}
              autoComplete={isSignIn ? 'current-password' : 'new-password'}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading}
              sx={{ py: 1.5, textTransform: 'none' }}
            >
              {loading ? (isSignIn ? 'Signing in…' : 'Creating account…') : (isSignIn ? 'Sign in' : 'Create account')}
            </Button>
          </Box>

          <Divider sx={{ my: 3 }}>or</Divider>

          {!isDemoMode && (
            <Button
              variant="text"
              fullWidth
              size="large"
              onClick={startWithoutAnAccount}
              disabled={starting}
              sx={{ textTransform: 'none', py: 1.5, mb: 1.5, fontWeight: 600 }}
            >
              {starting ? 'Opening the builder…' : 'Continue without an account'}
            </Button>
          )}

          <Button
            variant="outlined"
            fullWidth
            size="large"
            startIcon={<GoogleIcon />}
            onClick={loginWithGoogle}
            sx={{
              textTransform: 'none',
              py: 1.5,
              color: '#757575',
              borderColor: '#dadce0',
              '&:hover': { backgroundColor: '#f8f9fa', borderColor: '#dadce0' }
            }}
          >
            Continue with Google
          </Button>

          <Typography variant="caption" display="block" align="center" sx={{ mt: 3 }} color="text.secondary">
            By using FlowGate, you agree to our{' '}
            <Box
              component="a"
              href="https://flowgate.co.uk/terms"
              target="_blank"
              rel="noreferrer"
              sx={{ color: 'primary.main', fontWeight: 600, textDecoration: 'none' }}
            >
              Terms of Service
            </Box>
            {' '}and{' '}
            <Box
              component="a"
              href="https://flowgate.co.uk/privacy"
              target="_blank"
              rel="noreferrer"
              sx={{ color: 'primary.main', fontWeight: 600, textDecoration: 'none' }}
            >
              Privacy Policy
            </Box>
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default LoginPage;
